import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/helper/api";
import {
  AssignmentPlan,
  AssignmentSnapshot,
  initialPlan,
  planChanges,
} from "@/components/appointmentRequest/assignmentPlanner/assignmentPlan";

const emptyPlan: AssignmentPlan = {};

export function useAssignmentPlan(
  requestId: string,
  companyId: string | number,
) {
  const [snapshot, setSnapshot] = useState<AssignmentSnapshot>();
  const [history, setHistory] = useState<AssignmentPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>();
  const [reload, setReload] = useState(0);
  const active = useRef(0);
  const inFlight = useRef(false);
  useEffect(() => {
    const version = ++active.current;
    inFlight.current = false;
    setSaving(false);
    setLoading(true);
    setSnapshot(undefined);
    setHistory([]);
    setError(undefined);
    api.genericApiRequest({
      entity: "appointmentRequest",
      entityId: requestId,
      method: "get",
      additionalRouteParts: { assignment_plan: "/" },
      successHandler: (response: { data: AssignmentSnapshot }) => {
        if (active.current !== version) return;
        setSnapshot(response.data);
        setHistory([initialPlan(response.data)]);
        setLoading(false);
      },
      failHandler: (failure: unknown) => {
        if (active.current === version) {
          setError(failure);
          setLoading(false);
        }
      },
    });
    return () => {
      active.current++;
    };
  }, [requestId, companyId, reload]);
  const plan = history.at(-1) ?? emptyPlan;
  const changes = useMemo(
    () => (snapshot ? planChanges(snapshot, plan) : []),
    [snapshot, plan],
  );
  const update = useCallback((next: AssignmentPlan) => {
    if (!inFlight.current)
      setHistory((previous) =>
        previous.at(-1) === next ? previous : [...previous, next],
      );
  }, []);
  const confirm = (onSuccess: () => void) => {
    if (!snapshot || !changes.length || inFlight.current) return;
    inFlight.current = true;
    setSaving(true);
    setError(undefined);
    const version = active.current;
    api.genericApiRequest({
      entity: "appointmentRequest",
      entityId: requestId,
      method: "post",
      additionalRouteParts: { assignment_plan: "/" },
      submitData: { changes },
      successHandler: (response: { data: AssignmentSnapshot }) => {
        if (active.current !== version) return;
        inFlight.current = false;
        setSaving(false);
        setSnapshot(response.data);
        setHistory([initialPlan(response.data)]);
        onSuccess();
      },
      failHandler: (failure: unknown) => {
        if (active.current === version) {
          inFlight.current = false;
          setSaving(false);
          setError(failure);
        }
      },
    });
  };
  return {
    snapshot,
    plan,
    changes,
    loading,
    saving,
    error,
    update,
    confirm,
    canUndo: history.length > 1,
    undo: () => {
      if (!inFlight.current)
        setHistory((previous) =>
          previous.length > 1 ? previous.slice(0, -1) : previous,
        );
    },
    discard: () => {
      if (snapshot && !inFlight.current) setHistory([initialPlan(snapshot)]);
    },
    refresh: () => setReload((previous) => previous + 1),
  };
}
